import { useEffect, useState } from 'react';
import Sortable from 'sortablejs';

/**
 * In `<Sortable>` component use `onEnd` function and pass into it an `event` object which expose needed properties
 * @example
 * <ReactSortable
      list={widgetState}
      setList={setWidgetState}
      onEnd={(event) => handleReorderWidgets(event)}
		>
 * */

export const useSortableLocalUpdate = <T,>(
	data: readonly T[],
): {
	state: readonly T[];
	setState: React.Dispatch<React.SetStateAction<readonly T[]>>;
	optimisticReorder: (event: Sortable.SortableEvent, amountOfFilteredCoumns?: number) => T[] | undefined;
	updateFailed: (isOk: boolean | undefined) => void;
} => {
	/**
	 * Stores data from GrapghGL query
	 */
	const [state, setState] = useState<readonly T[]>(data.length > 0 ? data : []);
	const [prevState, setPrevState] = useState<readonly T[]>(data.length > 0 ? data : []);

	useEffect(() => {
		setState(data);
	}, [data]);

	/**
	 * Function which reorder list locally - prevents lag during server updates
	 * SortableJS takes into indexes also filtered fields, we need to subtract the amount from the indexes
	 */
	const optimisticReorder = (event: Sortable.SortableEvent, amountOfFilteredColumns = 0) => {
		if (event.oldIndex != null && event.newIndex != null) {
			const previousIndex = event.oldIndex - amountOfFilteredColumns;
			const newIndex = event.newIndex - amountOfFilteredColumns;
			const stateCopy = [...state];
			const itemToInsertToNewPosition = stateCopy[previousIndex];
			stateCopy.splice(previousIndex, 1);
			stateCopy.splice(newIndex, 0, itemToInsertToNewPosition);

			return stateCopy;
		}
	};

	const updateFailed = (isOk: boolean | undefined) => {
		if (isOk == null || isOk !== true) {
			setState(prevState);
			return;
		}

		setPrevState(state);
		return;
	};

	return { state, setState, optimisticReorder, updateFailed };
};
